import { Head, Link, router, useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Participant {
  id: number
  name: string | null
}

interface Activity {
  id: number
  notes: string | null
  activity_date: string | null
  user_name: string | null
  can_delete: boolean
}

interface LogDetail {
  id: number
  log_type: string
  log_date: string | null
  title: string | null
  content: string | null
  request: string | null
  request_date: string | null
  results_notes: string | null
  user_name: string | null
  user_id: number | null
  assigned_to_name: string | null
  assigned_to_id: number | null
  todo_category: string | null
  todo_category_id: number | null
  customer_user_id: number | null
  vendor_id: number | null
  status: number
  urgent: boolean
  sensitive: boolean
  significant: boolean
  completed: boolean
  completed_on: string | null
  completed_by_name: string | null
  due_date: string | null
  created_by_name: string | null
  created_on: string | null
  participants: Participant[]
  activities: Activity[]
  can_add_activity: boolean
  can_complete: boolean
}

interface Props {
  case_record: CaseRecord
  log: LogDetail
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleString()
}

const typeBadge = (type: string) => {
  const styles: Record<string, string> = {
    update: "bg-hpi-light-blue text-hpi-dark-blue",
    todo: "bg-hpi-yellow/30 text-hpi-dark-gray",
    restricted: "bg-hpi-red/20 text-hpi-red",
    accounting: "bg-hpi-dark-green/20 text-hpi-dark-green",
  }
  return (
    <span
      className={`rounded px-2 py-1 text-sm ${styles[type] ?? "bg-hpi-pale-gray text-hpi-dark-gray"}`}
    >
      {type}
    </span>
  )
}

export default function Show({ case_record, log }: Props) {
  const [showCompleteForm, setShowCompleteForm] = useState(false)

  const activityForm = useForm({ notes: "" })
  const completeForm = useForm({ results_notes: "", confirmed: false })

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Logs", href: `/cases/${case_record.id}/logs` },
    {
      title: log.title ?? `Log #${log.id}`,
      href: `/cases/${case_record.id}/logs/${log.id}`,
    },
  ]

  const basePath = `/cases/${case_record.id}/logs`

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this log?")) {
      router.delete(`${basePath}/${log.id}`)
    }
  }

  const handleAddActivity = (e: React.FormEvent) => {
    e.preventDefault()
    activityForm.post(`${basePath}/${log.id}/add_activity`, {
      onSuccess: () => activityForm.reset(),
      preserveScroll: true,
    })
  }

  const handleDeleteActivity = (activityId: number) => {
    if (confirm("Delete this activity note?")) {
      router.delete(`${basePath}/${log.id}/destroy_activity`, {
        data: { activity_id: activityId },
        preserveScroll: true,
      })
    }
  }

  const handleComplete = (e: React.FormEvent) => {
    e.preventDefault()
    if (!completeForm.data.confirmed) return
    router.patch(`${basePath}/${log.id}/complete`, {
      results_notes: completeForm.data.results_notes,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Log - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">
              {log.title ?? `Log #${log.id}`}
            </h1>
            {typeBadge(log.log_type)}
            {log.urgent && (
              <span className="bg-hpi-red/20 text-hpi-red rounded px-2 py-1 text-sm">
                Urgent
              </span>
            )}
            {log.sensitive && (
              <span className="rounded bg-orange-100 px-2 py-1 text-sm text-orange-700">
                Sensitive
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Link
              href={`/cases/${case_record.id}/time_entries/new`}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Log Time
            </Link>
            <Link
              href={`/cases/${case_record.id}/expenses/new`}
              className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Log Expense
            </Link>
            <Link
              href={`${basePath}/${log.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            {log.log_type === "todo" && !log.completed && log.can_complete && (
              <button
                onClick={() => setShowCompleteForm(true)}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Complete
              </button>
            )}
            <button
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Complete Todo Form */}
        {showCompleteForm && (
          <div className="border-hpi-dark-green mb-6 rounded border-2 bg-green-50 p-4">
            <h2 className="text-hpi-dark-green mb-3 font-semibold">
              Complete Todo
            </h2>
            <form onSubmit={handleComplete}>
              <div className="mb-3">
                <label
                  htmlFor="results_notes"
                  className="text-hpi-dark-gray mb-1 block text-sm font-medium"
                >
                  Results / Completion Notes{" "}
                  <span className="text-hpi-red">*</span>
                </label>
                <textarea
                  id="results_notes"
                  className="border-hpi-light-gray w-full rounded border p-2"
                  rows={4}
                  value={completeForm.data.results_notes}
                  onChange={(e) =>
                    completeForm.setData("results_notes", e.target.value)
                  }
                  required
                  placeholder="Enter completion notes..."
                />
              </div>
              <div className="mb-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={completeForm.data.confirmed}
                    onChange={(e) =>
                      completeForm.setData("confirmed", e.target.checked)
                    }
                  />
                  I confirm this todo is complete
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={
                    !completeForm.data.confirmed ||
                    !completeForm.data.results_notes.trim() ||
                    completeForm.processing
                  }
                  className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white disabled:opacity-50"
                >
                  Mark Complete
                </button>
                <button
                  type="button"
                  onClick={() => setShowCompleteForm(false)}
                  className="border-hpi-light-gray text-hpi-dark-gray rounded border px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            {log.log_type !== "todo" && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">
                  Content
                </h2>
                <div className="whitespace-pre-wrap">{log.content}</div>
              </div>
            )}

            {log.request && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">
                  Request
                </h2>
                <div className="whitespace-pre-wrap">{log.request}</div>
                {log.request_date && (
                  <p className="text-hpi-med-gray mt-2 text-sm">
                    Request date: {formatDate(log.request_date)}
                  </p>
                )}
              </div>
            )}

            {log.results_notes && (
              <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-2 font-semibold">
                  Results Notes
                </h2>
                <div className="whitespace-pre-wrap">{log.results_notes}</div>
              </div>
            )}

            {/* Activities section — always show for todos */}
            <div className="border-hpi-light-gray mb-6 rounded border bg-white p-4">
              <h2 className="text-hpi-dark-gray mb-2 font-semibold">
                Activity Notes ({log.activities.length})
              </h2>
              {log.activities.length > 0 ? (
                <div className="space-y-3">
                  {log.activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="border-hpi-light-gray border-l-2 pl-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-hpi-med-gray text-sm">
                          {activity.user_name} -{" "}
                          {formatDateTime(activity.activity_date)}
                        </div>
                        {activity.can_delete && (
                          <button
                            onClick={() => handleDeleteActivity(activity.id)}
                            className="text-hpi-red hover:text-hpi-red/80 text-xs"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <div className="whitespace-pre-wrap">
                        {activity.notes}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-hpi-med-gray text-sm">
                  No activity notes yet.
                </p>
              )}

              {/* Add Activity Form */}
              {log.can_add_activity && (
                <form
                  onSubmit={handleAddActivity}
                  className="mt-4 border-t pt-4"
                >
                  <label
                    htmlFor="activity_notes"
                    className="text-hpi-dark-gray mb-1 block text-sm font-medium"
                  >
                    Add Note
                  </label>
                  <textarea
                    id="activity_notes"
                    className="border-hpi-light-gray mb-2 w-full rounded border p-2"
                    rows={3}
                    value={activityForm.data.notes}
                    onChange={(e) =>
                      activityForm.setData("notes", e.target.value)
                    }
                    required
                    placeholder="Enter activity note..."
                  />
                  <button
                    type="submit"
                    disabled={
                      !activityForm.data.notes.trim() || activityForm.processing
                    }
                    className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-sm text-white disabled:opacity-50"
                  >
                    Add Note
                  </button>
                </form>
              )}
            </div>
          </div>

          <div>
            <div className="border-hpi-light-gray rounded border bg-white p-4">
              <h2 className="text-hpi-dark-gray mb-3 font-semibold">Details</h2>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-hpi-med-gray">Author</dt>
                  <dd>{log.user_name}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Log Date</dt>
                  <dd>{formatDateTime(log.log_date)}</dd>
                </div>
                <div>
                  <dt className="text-hpi-med-gray">Created</dt>
                  <dd>
                    {formatDateTime(log.created_on)} by {log.created_by_name}
                  </dd>
                </div>
                {log.log_type === "todo" && (
                  <>
                    {log.assigned_to_name && (
                      <div>
                        <dt className="text-hpi-med-gray">Assigned To</dt>
                        <dd>{log.assigned_to_name}</dd>
                      </div>
                    )}
                    {log.due_date && (
                      <div>
                        <dt className="text-hpi-med-gray">Due Date</dt>
                        <dd>{formatDate(log.due_date)}</dd>
                      </div>
                    )}
                    {log.todo_category && (
                      <div>
                        <dt className="text-hpi-med-gray">Category</dt>
                        <dd>{log.todo_category}</dd>
                      </div>
                    )}
                  </>
                )}
                {log.completed && (
                  <div>
                    <dt className="text-hpi-med-gray">Completed</dt>
                    <dd>
                      {formatDateTime(log.completed_on)} by{" "}
                      {log.completed_by_name}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {log.participants.length > 0 && (
              <div className="border-hpi-light-gray mt-4 rounded border bg-white p-4">
                <h2 className="text-hpi-dark-gray mb-3 font-semibold">
                  Participants ({log.participants.length})
                </h2>
                <ul className="space-y-1 text-sm">
                  {log.participants.map((p) => (
                    <li key={p.id}>{p.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}/logs`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Logs
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
